/**
 * Import report collector of the package importer (W1-07b formats-package; DESIGN.md §4.17, §7.2): entries of the
 * five classes in the order they were found, equal findings (class, message key, line, path without list indices)
 * folded into one entry with a count, whether a finding was already reported (`has`), and whether Phira itself
 * would have loaded the package.
 */

import type { ImportClass, ImportReportEntry } from '../../core/ir/types';

export interface ReportSink {
  add(
    cls: ImportClass,
    key: string,
    params?: Readonly<Record<string, string | number>>,
    path?: string,
    line?: number,
    count?: number,
  ): void;
  /** Phira would not load (or play) the package as it was given. */
  wouldNotLoad(): void;
}

interface Folded {
  readonly entry: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] };
  count: number;
}

export class PackageReport implements ReportSink {
  private readonly folded = new Map<string, Folded>();
  private readonly order: string[] = [];
  private loads = true;

  private push(entry: ImportReportEntry, count: number): void {
    const pattern = entry.path === undefined ? '' : entry.path.replace(/\[\d+\]/g, '[]');
    const id = `${entry.cls}|${entry.message.key}|${entry.line === undefined ? '' : String(entry.line)}|${pattern}|${
      entry.message.params === undefined ? '' : JSON.stringify(entry.message.params)
    }`;
    const prev = this.folded.get(id);
    if (prev !== undefined) {
      prev.count += count;
      return;
    }
    this.folded.set(id, { entry: { ...entry }, count });
    this.order.push(id);
  }

  add(
    cls: ImportClass,
    key: string,
    params?: Readonly<Record<string, string | number>>,
    path?: string,
    line?: number,
    count = 1,
  ): void {
    const entry: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] } = {
      cls,
      message: params === undefined ? { key } : { key, params },
    };
    if (path !== undefined) entry.path = path;
    if (line !== undefined) entry.line = line;
    this.push(entry, count);
  }

  /** Adds entries produced elsewhere (normalisation, lift, legacy importers), keeping their counts. */
  addAll(entries: readonly ImportReportEntry[]): void {
    for (const e of entries) this.push(e, e.count ?? 1);
  }

  /** True when an entry of this class and message key (on this line, when given) was reported. */
  has(cls: ImportClass, key: string, line?: number): boolean {
    for (const f of this.folded.values()) {
      const e = f.entry;
      if (e.cls === cls && e.message.key === key && (line === undefined || e.line === line)) return true;
    }
    return false;
  }

  wouldNotLoad(): void {
    this.loads = false;
  }

  get phiraWouldLoad(): boolean {
    return this.loads;
  }

  entries(): ImportReportEntry[] {
    const out: ImportReportEntry[] = [];
    for (const id of this.order) {
      const f = this.folded.get(id);
      if (f === undefined) continue;
      const e: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] } = { ...f.entry };
      if (f.count > 1) e.count = f.count;
      else delete e.count;
      out.push(e);
    }
    return out;
  }
}
